import { create } from "zustand";
import { apiClient } from "../lib/api-client";
import { encrypt, decrypt, getStoredKey } from "../lib/crypto";
import { useAuthStore } from "./auth-store";
import { isExpenseForReports, isIncomeForReports } from "../lib/transaction-types";
import { filterByStatus, getAccountTransactionBalance, getEffectiveTransactionDate } from "../lib/finance-calculations";
import { generateNextCategoryCode, normalizeCategoriesWithCodes } from "../lib/category-tree";
import { planCategoryClone } from "../lib/category-clone";
import { buildAccountIdBackfill } from "../lib/account-relations";
import { buildCategoryIdBackfill, getTransactionCategory } from "../lib/category-relations";
import { buildAmountSignBackfill, withAmountSign } from "../lib/transaction-sign";
import { formatCategoryName, normalizeCategoryName, normalizeUppercase, parseCurrency, stringToColor } from "../lib/utils";

// Tenant ativo via header explícito — autenticação em si é via cookie httpOnly
// (apiClient já envia withCredentials, sem header Authorization a montar aqui).
const tenantHeaders = () => {
  const { activeTenant } = useAuthStore.getState();
  return activeTenant?.id ? { headers: { "X-Tenant-ID": activeTenant.id } } : undefined;
};

const normalizeAccountPayload = (data = {}) => ({
  nature: "D",
  currency: "BRL",
  bankIconId: null,
  active: true,
  showOnHome: true,
  showOnDashboard: true,
  ...data,
  name: normalizeUppercase(data.name),
  bank: normalizeUppercase(data.bank),
  nature: data.nature || "D",
  currency: data.currency || "BRL",
  bankIconId: data.bankIconId || null,
  active: data.active !== false,
  showOnHome: data.showOnHome !== false,
  showOnDashboard: data.showOnDashboard !== false,
});

const stripTransactionTransportFields = (data = {}) => {
  const {
    id,
    user_id,
    tenant_id,
    type,
    encrypted_payload,
    iv,
    created_at,
    updated_at,
    deleted_at,
    ...payload
  } = data;
  return payload;
};

const normalizeTransactionForWrite = (type, data = {}) => withAmountSign(type, data);

const maybeBackfillTransactions = async (get) => {
  const { transactions, accounts, categories } = get();
  if (!transactions.length) return;

  const withSigns = buildAmountSignBackfill(transactions);
  const withAccounts = accounts.length ? buildAccountIdBackfill(withSigns, accounts) : withSigns;
  const backfilled = categories.length ? buildCategoryIdBackfill(withAccounts, categories) : withAccounts;
  const updates = backfilled
    .filter((tx, index) => tx !== transactions[index])
    .map((tx) => ({
      id: tx.id,
      type: tx.type,
      data: stripTransactionTransportFields(tx),
    }));

  if (!updates.length) return;
  await get().updateTransactionBatch(updates);
};


export const useFinanceStore = create((set, get) => ({
  // State
  transactions: [],
  accounts: [],
  categories: [],
  cards: [],
  isLoading: false,
  error: null,
  isDecrypting: false,

  // ==================== TRANSACTIONS ====================

  fetchTransactions: async () => {
    const key = getStoredKey();
    if (!key) {
      set({ error: "Chave de criptografia não encontrada. Faça login novamente." });
      return;
    }

    set({ isLoading: true, isDecrypting: true });
    try {
      const response = await apiClient.get("/transactions", tenantHeaders());

      // Descriptografa todas as transações
      const decrypted = await Promise.all(
        response.data.map(async (tx) => {
          const data = await decrypt(tx.encrypted_payload, tx.iv, key);
          return {
            ...tx,
            ...data, // description, amount, category, account, date
          };
        })
      );

      set({ transactions: decrypted, isLoading: false, isDecrypting: false });
      await maybeBackfillTransactions(get);
    } catch (error) {
      set({
        error: error.response?.data?.detail || "Erro ao carregar transações",
        isLoading: false,
        isDecrypting: false
      });
    }
  },

  addTransaction: async (type, data) => {
    const key = getStoredKey();
    if (!key) {
      set({ error: "Chave de criptografia não encontrada" });
      return { success: false };
    }

    try {
      const transactionData = normalizeTransactionForWrite(type, data);
      const { encrypted_payload, iv } = await encrypt(transactionData, key);

      const response = await apiClient.post(
        "/transactions",
        { type, encrypted_payload, iv },
        tenantHeaders()
      );

      const newTx = { ...response.data, ...transactionData };
      set((state) => ({
        transactions: [newTx, ...state.transactions],
      }));

      return { success: true };
    } catch (error) {
      set({ error: error.response?.data?.detail || "Erro ao adicionar transação" });
      return { success: false };
    }
  },

  addTransactionBatch: async (transactionsToAdd) => {
    const key = getStoredKey();
    if (!key) return { success: false };

    try {
      const payloadArray = await Promise.all(
        transactionsToAdd.map(async (txData) => {
          const transactionData = normalizeTransactionForWrite(txData.type, txData.data);
          const { encrypted_payload, iv } = await encrypt(transactionData, key);
          return {
            type: txData.type,
            encrypted_payload,
            iv,
            data: transactionData,
          };
        })
      );

      const response = await apiClient.post(
        "/transactions/batch",
        payloadArray.map(({ data, ...payload }) => payload),
        tenantHeaders()
      );

      set((state) => {
        const newTxs = response.data.map((tx, index) => ({
          ...tx,
          ...payloadArray[index].data
        }));
        return {
          transactions: [...newTxs, ...state.transactions],
        };
      });

      return { success: true };
    } catch (error) {
      set({ error: error.response?.data?.detail || "Erro ao adicionar transações em lote" });
      return { success: false };
    }
  },

  parseCardInvoicePdf: async (file) => {
    if (!file) return { success: false, items: [], error: "Selecione um arquivo PDF." };

    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await apiClient.post(
        "/cards/invoice-import/parse",
        formData,
        tenantHeaders()
      );

      return { success: true, items: response.data?.items || [] };
    } catch (error) {
      const message = error.response?.data?.detail || "Erro ao importar fatura";
      set({ error: message });
      return { success: false, items: [], error: message };
    }
  },

  parseAccountStatementFile: async (file) => {
    if (!file) return { success: false, items: [], error: "Selecione um arquivo OFX ou PDF." };

    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await apiClient.post(
        "/accounts/statement-import/parse",
        formData,
        tenantHeaders()
      );

      return { success: true, items: response.data?.items || [] };
    } catch (error) {
      const message = error.response?.data?.detail || "Erro ao importar extrato";
      set({ error: message });
      return { success: false, items: [], error: message };
    }
  },

  updateTransaction: async (id, type, data) => {
    const key = getStoredKey();
    if (!key) return { success: false };

    try {
      const transactionData = normalizeTransactionForWrite(type, data);
      const { encrypted_payload, iv } = await encrypt(transactionData, key);

      const response = await apiClient.put(
        `/transactions/${id}`,
        { type, encrypted_payload, iv },
        tenantHeaders()
      );

      set((state) => ({
        transactions: state.transactions.map((tx) =>
          tx.id === id ? { ...response.data, ...transactionData } : tx
        ),
      }));

      return { success: true };
    } catch (error) {
      set({ error: error.response?.data?.detail || "Erro ao atualizar transação" });
      return { success: false };
    }
  },

  deleteTransaction: async (id, scope = "current") => {
    const transactions = get().transactions;
    const tx = transactions.find((item) => item.id === id);
    if (!tx) return { success: false };

    const series = tx.recurrence_id
      ? transactions.filter((item) => item.recurrence_id === tx.recurrence_id)
      : [tx];
    const scopedSeries = scope === "all"
      ? series
      : scope === "future"
        ? series.filter((item) => new Date(item.date) >= new Date(tx.date))
        : [tx];
    const ids = new Set();

    scopedSeries.forEach((item) => {
      ids.add(item.id);
      if (!item.linked_transfer_id) return;
      transactions
        .filter((candidate) => candidate.linked_transfer_id === item.linked_transfer_id)
        .forEach((candidate) => ids.add(candidate.id));
    });

    try {
      for (const transactionId of ids) {
        await apiClient.delete(`/transactions/${transactionId}`, tenantHeaders());
      }
      set((state) => ({
        transactions: state.transactions.filter((item) => !ids.has(item.id)),
      }));
      return { success: true };
    } catch (error) {
      set({ error: error.response?.data?.detail || "Erro ao excluir transação" });
      return { success: false };
    }
  },

  updateTransactionBatch: async (transactionsToUpdate) => {
    const key = getStoredKey();
    if (!key) return { success: false };

    try {
      const payloadArray = await Promise.all(
        transactionsToUpdate.map(async (txData) => {
          const transactionData = normalizeTransactionForWrite(txData.type, txData.data);
          const { encrypted_payload, iv } = await encrypt(transactionData, key);
          return {
            id: txData.id,
            type: txData.type,
            encrypted_payload,
            iv,
            data: transactionData,
          };
        })
      );

      await apiClient.put(
        "/transactions/batch",
        payloadArray.map(({ data, ...payload }) => payload),
        tenantHeaders()
      );

      set((state) => {
        const updatedIds = transactionsToUpdate.map(t => t.id);
        const mapUpdates = new Map();
        payloadArray.forEach(t => mapUpdates.set(t.id, t.data));

        return {
          transactions: state.transactions.map((tx) =>
            updatedIds.includes(tx.id) ? { ...tx, ...mapUpdates.get(tx.id) } : tx
          ),
        };
      });

      return { success: true };
    } catch (error) {
      set({ error: error.response?.data?.detail || "Erro ao atualizar transações em lote" });
      return { success: false };
    }
  },

  // ==================== ACCOUNTS ====================

  fetchAccounts: async () => {
    const key = getStoredKey();
    if (!key) return;

    set({ isLoading: true });
    try {
      const response = await apiClient.get("/accounts", tenantHeaders());

      const decrypted = await Promise.all(
        response.data.map(async (acc) => {
          const data = await decrypt(acc.encrypted_payload, acc.iv, key);
          return normalizeAccountPayload({ ...acc, ...data });
        })
      );

      set({ accounts: decrypted, isLoading: false });
      await maybeBackfillTransactions(get);
    } catch (error) {
      set({ error: error.response?.data?.detail || "Erro ao carregar contas", isLoading: false });
    }
  },

  addAccount: async (data) => {
    const key = getStoredKey();
    if (!key) return { success: false };

    try {
      const accountData = normalizeAccountPayload(data);
      const { encrypted_payload, iv } = await encrypt(accountData, key);

      const response = await apiClient.post(
        "/accounts",
        { encrypted_payload, iv },
        tenantHeaders()
      );

      const newAccount = { ...response.data, ...accountData };
      set((state) => ({
        accounts: [...state.accounts, newAccount],
      }));

      return { success: true, account: newAccount };
    } catch (error) {
      set({ error: error.response?.data?.detail || "Erro ao adicionar conta" });
      return { success: false };
    }
  },

  updateAccount: async (id, data) => {
    const key = getStoredKey();
    if (!key) return { success: false };

    try {
      const accountData = normalizeAccountPayload(data);
      const { encrypted_payload, iv } = await encrypt(accountData, key);

      const response = await apiClient.put(
        `/accounts/${id}`,
        { encrypted_payload, iv },
        tenantHeaders()
      );

      set((state) => ({
        accounts: state.accounts.map((acc) =>
          acc.id === id ? { ...response.data, ...accountData } : acc
        ),
      }));

      return { success: true };
    } catch (error) {
      set({ error: error.response?.data?.detail || "Erro ao atualizar conta" });
      return { success: false };
    }
  },

  deleteAccount: async (id) => {
    try {
      await apiClient.delete(`/accounts/${id}`, tenantHeaders());
      set((state) => ({
        accounts: state.accounts.filter((acc) => acc.id !== id),
      }));
      return { success: true };
    } catch (error) {
      set({ error: error.response?.data?.detail || "Erro ao excluir conta" });
      return { success: false };
    }
  },

  // ==================== CATEGORIES ====================

  fetchCategories: async () => {
    const key = getStoredKey();
    if (!key) return;

    try {
      const response = await apiClient.get("/categories", tenantHeaders());

      const decrypted = await Promise.all(
        response.data.map(async (cat) => {
          const data = await decrypt(cat.encrypted_payload, cat.iv, key);
          return { ...cat, ...data };
        })
      );

      set({ categories: normalizeCategoriesWithCodes(decrypted) });
      await maybeBackfillTransactions(get);
    } catch (error) {
      set({ error: error.response?.data?.detail || "Erro ao carregar categorias" });
    }
  },

  addCategory: async (type, data) => {
    const key = getStoredKey();
    if (!key) return { success: false };

    try {
      const parentId = data.parent_id || null;
      const categories = get().categories;
      const normalizedName = normalizeCategoryName(data.name);
      const duplicate = categories.some((category) => (
        category.type === type &&
        (category.parent_id || null) === parentId &&
        normalizeCategoryName(category.name) === normalizedName
      ));

      if (duplicate) {
        set({ error: "Já existe uma categoria com esse nome neste pai" });
        return { success: false };
      }

      const categoryData = {
        ...data,
        name: normalizedName,
        parent_id: parentId,
        codigo: data.codigo || generateNextCategoryCode(categories, type, parentId),
        color: data.color || stringToColor(normalizedName || "DEFAULT"),
      };
      const { encrypted_payload, iv } = await encrypt(categoryData, key);

      const response = await apiClient.post(
        "/categories",
        { type, encrypted_payload, iv },
        tenantHeaders()
      );

      const newCategory = { ...response.data, ...categoryData };
      set((state) => ({
        categories: normalizeCategoriesWithCodes([...state.categories, newCategory]),
      }));

      return { success: true, category: newCategory };
    } catch (error) {
      set({ error: error.response?.data?.detail || "Erro ao adicionar categoria" });
      return { success: false };
    }
  },

  updateCategory: async (id, data) => {
    const key = getStoredKey();
    if (!key) return { success: false };

    try {
      const existingCat = get().categories.find((c) => c.id === id);
      const normalizedName = normalizeCategoryName(data.name ?? existingCat?.name);
      const nextParentId = data.parent_id === undefined ? existingCat?.parent_id || null : data.parent_id;
      const duplicate = get().categories.some((category) => (
        category.id !== id &&
        category.type === (existingCat?.type || "expense") &&
        (category.parent_id || null) === (nextParentId || null) &&
        normalizeCategoryName(category.name) === normalizedName
      ));

      if (duplicate) {
        set({ error: "Já existe uma categoria com esse nome neste pai" });
        return { success: false };
      }

      const categoryData = {
        ...data,
        name: normalizedName,
        parent_id: nextParentId,
        codigo: data.codigo || existingCat?.codigo || generateNextCategoryCode(get().categories, existingCat?.type || "expense", nextParentId, [id]),
        color: data.color || existingCat?.color || stringToColor(normalizedName || "DEFAULT"),
      };
      const { encrypted_payload, iv } = await encrypt(categoryData, key);

      // Update uses PUT and expects { type, encrypted_payload, iv } similar to add but for update
      const response = await apiClient.put(
        `/categories/${id}`,
        { type: existingCat?.type || "expense", encrypted_payload, iv },
        tenantHeaders()
      );

      const updatedCategory = { ...response.data, ...categoryData };
      set((state) => ({
        categories: normalizeCategoriesWithCodes(state.categories.map((cat) =>
          cat.id === id ? updatedCategory : cat
        )),
      }));

      return { success: true, category: updatedCategory };
    } catch (error) {
      set({ error: error.response?.data?.detail || "Erro ao atualizar categoria" });
      return { success: false };
    }
  },

  cloneCategory: async (rootId) => {
    const key = getStoredKey();
    if (!key) return { success: false, planned: 0, created: 0 };

    const plan = planCategoryClone(get().categories, rootId);
    if (!plan.valid) {
      set({ error: plan.message });
      return { success: false, planned: 0, created: 0, error: plan.message };
    }

    const planned = plan.nodes.length;
    const createdIdBySourceId = new Map();
    const createdIds = [];

    for (const node of plan.nodes) {
      const parentId = node.parentSourceId
        ? createdIdBySourceId.get(node.parentSourceId)
        : node.parentId;

      // O código é recalculado pelo addCategory contra o estado já atualizado com os clones anteriores.
      const result = await get().addCategory(node.type, {
        name: node.name,
        parent_id: parentId || null,
        color: node.color,
      });

      if (!result.success) {
        const failureMessage = get().error || "Erro ao clonar categoria";
        // Rollback best-effort: remove os filhos antes dos pais.
        const remaining = [];
        for (const createdId of [...createdIds].reverse()) {
          const removal = await get().deleteCategory(createdId);
          if (!removal.success) remaining.push(createdId);
        }

        set({ error: failureMessage });
        return {
          success: false,
          planned,
          created: remaining.length,
          rolledBack: remaining.length === 0,
          error: failureMessage,
        };
      }

      createdIdBySourceId.set(node.sourceId, result.category.id);
      createdIds.push(result.category.id);
    }

    return { success: true, planned, created: createdIds.length };
  },

  deleteCategory: async (id) => {
    try {
      await apiClient.delete(`/categories/${id}`, tenantHeaders());
      set((state) => ({
        categories: state.categories.filter((cat) => cat.id !== id),
      }));
      return { success: true };
    } catch (error) {
      set({ error: error.response?.data?.detail || "Erro ao excluir categoria" });
      return { success: false };
    }
  },

  // ==================== CARDS ====================

  fetchCards: async () => {
    const key = getStoredKey();
    if (!key) return;

    try {
      const response = await apiClient.get("/cards", tenantHeaders());

      const decrypted = await Promise.all(
        response.data.map(async (card) => {
          const data = await decrypt(card.encrypted_payload, card.iv, key);
          return { ...card, ...data };
        })
      );

      set({ cards: decrypted });
    } catch (error) {
      set({ error: error.response?.data?.detail || "Erro ao carregar cartões" });
    }
  },

  addCard: async (data) => {
    const key = getStoredKey();
    if (!key) return { success: false };

    try {
      const { encrypted_payload, iv } = await encrypt(data, key);

      const response = await apiClient.post(
        "/cards",
        { encrypted_payload, iv },
        tenantHeaders()
      );

      set((state) => ({
        cards: [...state.cards, { ...response.data, ...data }],
      }));

      return { success: true };
    } catch (error) {
      set({ error: error.response?.data?.detail || "Erro ao adicionar cartão" });
      return { success: false };
    }
  },

  deleteCard: async (id) => {
    try {
      await apiClient.delete(`/cards/${id}`, tenantHeaders());
      set((state) => ({
        cards: state.cards.filter((card) => card.id !== id),
      }));
      return { success: true };
    } catch (error) {
      set({ error: error.response?.data?.detail || "Erro ao excluir cartão" });
      return { success: false };
    }
  },

  updateCard: async (id, data) => {
    const key = getStoredKey();
    if (!key) return { success: false };

    try {
      const { encrypted_payload, iv } = await encrypt(data, key);

      const response = await apiClient.put(
        `/cards/${id}`,
        { encrypted_payload, iv },
        tenantHeaders()
      );

      set((state) => ({
        cards: state.cards.map((card) =>
          card.id === id ? { ...response.data, ...data } : card
        ),
      }));

      return { success: true };
    } catch (error) {
      set({ error: error.response?.data?.detail || "Erro ao atualizar cartão" });
      return { success: false };
    }
  },

  // ==================== COMPUTED VALUES ====================

  // Todas as agregações são calculadas no frontend após descriptografia
  getBalance: (realizadoOnly = true) => {
    const { transactions } = get();
    const filtered = filterByStatus(transactions, realizadoOnly);
    return filtered.reduce((acc, tx) => {
      const amount = parseCurrency(tx.amount);
      if (isIncomeForReports(tx)) return acc + amount;
      if (isExpenseForReports(tx)) return acc - amount;
      return acc;
    }, 0);
  },

  getTotalIncome: (realizadoOnly = true) => {
    const { transactions } = get();
    const filtered = filterByStatus(transactions, realizadoOnly);
    return filtered
      .filter(isIncomeForReports)
      .reduce((acc, tx) => acc + parseCurrency(tx.amount), 0);
  },

  getTotalExpense: (realizadoOnly = true) => {
    const { transactions } = get();
    const filtered = filterByStatus(transactions, realizadoOnly);
    return filtered
      .filter(isExpenseForReports)
      .reduce((acc, tx) => acc + parseCurrency(tx.amount), 0);
  },

  getMonthlyData: (realizadoOnly = false) => {
    const { transactions } = get();
    const filtered = filterByStatus(transactions, realizadoOnly);
    const monthlyMap = new Map();

    filtered
      .filter((tx) => isIncomeForReports(tx) || isExpenseForReports(tx))
      .forEach((tx) => {
        const date = new Date(getEffectiveTransactionDate(tx, realizadoOnly));
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;

        if (!monthlyMap.has(monthKey)) {
          monthlyMap.set(monthKey, { month: monthKey, income: 0, expense: 0 });
        }

        const data = monthlyMap.get(monthKey);
        const amount = parseCurrency(tx.amount);

        if (isIncomeForReports(tx)) {
          data.income += amount;
        } else if (isExpenseForReports(tx)) {
          data.expense += amount;
        }
      });

    return Array.from(monthlyMap.values())
      .sort((a, b) => a.month.localeCompare(b.month))
      .slice(-6); // Últimos 6 meses
  },

  getCategoryData: (type = "expense", realizadoOnly = false) => {
    const { transactions, categories } = get();
    const filtered = filterByStatus(transactions, realizadoOnly);
    const categoryMap = new Map();

    filtered
      .filter((tx) => (type === "income" ? isIncomeForReports(tx) : isExpenseForReports(tx)))
      .forEach((tx) => {
        const category = getTransactionCategory(tx, categories);
        const name = formatCategoryName(category?.name || tx.category);
        const key = category?.id || name;
        const parent = category?.parent_id
          ? categories.find((item) => item.id === category.parent_id)
          : null;
        const current = categoryMap.get(key) || {
          name,
          value: 0,
          color: category?.color || parent?.color || stringToColor(name),
        };
        categoryMap.set(key, {
          ...current,
          value: current.value + parseCurrency(tx.amount),
        });
      });

    return Array.from(categoryMap.values())
      .sort((a, b) => b.value - a.value);
  },

  getAccountBalance: (accountOrName, realizadoOnly = true) => {
    const { transactions, accounts } = get();
    return getAccountTransactionBalance(transactions, accountOrName, realizadoOnly, { accounts });
  },

  // Limpa dados
  clearData: () => {
    set({
      transactions: [],
      accounts: [],
      categories: [],
      cards: [],
      error: null,
    });
  },

  clearError: () => set({ error: null }),
}));
